import { useState } from 'react';
import { SCENARIO_MATRIX, PROJECT_SCOPE } from '../../data/sessions';
import { C, Badge, Card } from './shared';

export default function TestingTab() {
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const API = process.env.REACT_APP_API_URL || 'https://api.marketpulze.com';
  const SITE = 'https://marketpulze.com';

  const runChecks = async () => {
    setLoading(true);
    setResults(null);
    const checks = [
      { name: 'Homepage loads', url: SITE, expect: 'MarketPulze' },
      { name: 'Backend /health', url: API + '/health', expect: 'healthy' },
      { name: 'SEO endpoint AAPL', url: API + '/api/seo/stocks/AAPL', expect: 'ticker' },
      { name: 'Chart NVDA', url: API + '/api/seo/NVDA/chart?resolution=D', expect: 'candles' },
      { name: 'News NVDA', url: API + '/api/seo/NVDA/news', expect: 'articles' },
      { name: 'Earnings NVDA', url: API + '/api/seo/NVDA/earnings', expect: 'earnings' },
      { name: 'Consensus NVDA', url: API + '/api/seo/NVDA/consensus', expect: 'consensus' },
      { name: 'Market overview', url: API + '/api/market/overview', expect: 'success' },
    ];
    const out = await Promise.all(checks.map(async c => {
      const t0 = Date.now();
      try {
        const r = await fetch(c.url, { signal: AbortSignal.timeout(10000) });
        const ms = Date.now() - t0;
        const body = await r.text();
        const ok = r.status === 200 && body.includes(c.expect);
        return { name: c.name, ok, ms, status: r.status };
      } catch (e) {
        return { name: c.name, ok: false, ms: Date.now() - t0, status: 'ERR', error: e.message?.slice(0,40) };
      }
    }));
    setResults(out);
    setLoading(false);
  };

  const allScenarios = Object.entries(SCENARIO_MATRIX).flatMap(([group, items]) =>
    items.map(s => ({ ...s, group }))
  );

  const sc = PROJECT_SCOPE.sdlcStrategy;

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <Card title="Live API Health Check" accent={C.green}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
          <button onClick={runChecks} disabled={loading}
            style={{ padding: '8px 20px',
              background: loading ? C.muted : C.green,
              border: 'none', borderRadius: 8, color: '#050d1a',
              fontWeight: 800, cursor: loading ? 'default' : 'pointer',
              fontSize: 13, fontFamily: 'inherit' }}>
            {loading ? 'Running...' : 'Run Health Check'}
          </button>
          {results && (
            <span style={{ color: results.every(r => r.ok) ? C.green : C.warning, fontWeight: 700, fontSize: 13 }}>
              {results.filter(r => r.ok).length}/{results.length} passing
            </span>
          )}
        </div>
        {!results && !loading && (
          <div style={{ color: C.muted, fontSize: 12 }}>
            Click to ping all live endpoints and verify site health.
          </div>
        )}
        {results && results.map((r, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10,
            padding: '6px 0', borderBottom: '1px solid ' + C.border + '22' }}>
            <span style={{ color: r.ok ? C.green : C.danger, fontSize: 13, minWidth: 16 }}>
              {r.ok ? 'OK' : 'FAIL'}
            </span>
            <span style={{ color: C.text, fontSize: 12, flex: 1 }}>{r.name}</span>
            <span style={{ color: r.ms < 500 ? C.green : r.ms < 2000 ? C.warning : C.danger,
              fontSize: 11, fontFamily: 'monospace' }}>{r.ms}ms</span>
            <span style={{ color: r.ok ? C.green : C.danger, fontSize: 11 }}>
              {r.status}{r.error ? ' — ' + r.error : ''}
            </span>
          </div>
        ))}
      </Card>

      <Card title={'Scenario Coverage — ' + allScenarios.length + ' total'} accent={C.info}>
        <div style={{ color: C.muted, fontSize: 11, marginBottom: 10 }}>
          Source: SCENARIO_MATRIX in src/data/sessions.js. Update status there after each session validates scenarios.
        </div>
        {Object.entries(SCENARIO_MATRIX).map(([group, items]) => (
          <div key={group} style={{ marginBottom: 14 }}>
            <div style={{ color: C.info, fontWeight: 700, fontSize: 11,
              letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 6,
              padding: '4px 0', borderBottom: '1px solid ' + C.border }}>{group}</div>
            {items.map(s => (
              <div key={s.id} style={{ display: 'flex', gap: 10, padding: '5px 0',
                borderBottom: '1px solid ' + C.border + '11' }}>
                <span style={{ color: C.muted, fontSize: 10, fontFamily: 'monospace',
                  flexShrink: 0, minWidth: 50 }}>{s.id}</span>
                <span style={{ color: C.muted, fontSize: 11, flexShrink: 0, minWidth: 20 }}>{s.status}</span>
                <span style={{ color: C.subtle, fontSize: 11, lineHeight: 1.5 }}>{s.desc}</span>
              </div>
            ))}
          </div>
        ))}
      </Card>

      <Card title="Testing Strategy — Current to Future" accent={C.warning}>
        {sc.testingLevels.map(t => (
          <div key={t.level} style={{ display: 'grid',
            gridTemplateColumns: '140px 80px 1fr', gap: 10,
            padding: '8px 0', borderBottom: '1px solid ' + C.border + '22' }}>
            <span style={{ color: C.text, fontWeight: 700, fontSize: 12 }}>{t.level}</span>
            <Badge color={t.current === true ? C.green : t.current === 'manual' ? C.warning : C.muted}>
              {t.current === true ? 'ACTIVE' : t.current === 'manual' ? 'MANUAL' : 'FUTURE'}
            </Badge>
            <span style={{ color: C.muted, fontSize: 11 }}>{t.description} — {t.tool}</span>
          </div>
        ))}
      </Card>

      <Card title="SDLC Pipeline" accent={C.purple}>
        {[
          { phase: 'Current (Phase A/B)', model: sc.current.model, desc: sc.current.description, c: C.green },
          { phase: 'Phase C+', model: sc.phaseC.model, desc: sc.phaseC.description, c: C.warning },
          { phase: 'Phase D', model: sc.phaseD.model, desc: sc.phaseD.description, c: C.purple },
        ].map(p => (
          <div key={p.phase} style={{ display: 'grid', gridTemplateColumns: '140px 180px 1fr',
            gap: 12, padding: '8px 0', borderBottom: '1px solid ' + C.border + '22' }}>
            <Badge color={p.c}>{p.phase}</Badge>
            <span style={{ color: p.c, fontWeight: 700, fontSize: 12, fontFamily: 'monospace' }}>{p.model}</span>
            <span style={{ color: C.muted, fontSize: 11 }}>{p.desc}</span>
          </div>
        ))}
      </Card>

      <Card title="Incident Log" accent={C.danger}>
        <div style={{ color: C.muted, fontSize: 11, marginBottom: 10 }}>
          Full rollback procedure in INCIDENT.md at repo root.
        </div>
        {[
          { date: 'Mar 17 2026', what: 'Blank screen on marketpulze.com', cause: 'RoadmapPage.jsx surrogate char encoding from Python string patch', fix: 'git checkout 9b0824d -- RoadmapPage.jsx', commit: 'ff92f2c', severity: 'HIGH' },
        ].map((inc, i) => (
          <div key={i} style={{ background: C.surface, borderRadius: 8, padding: 12,
            borderLeft: '3px solid ' + C.danger, marginBottom: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
              <span style={{ color: C.danger, fontWeight: 700, fontSize: 12 }}>{inc.date} — {inc.what}</span>
              <Badge color={C.danger}>{inc.severity}</Badge>
            </div>
            <div style={{ color: C.muted, fontSize: 11 }}>Cause: {inc.cause}</div>
            <div style={{ color: C.muted, fontSize: 11 }}>Fix: <span style={{ fontFamily: 'monospace', color: C.info }}>{inc.fix}</span></div>
          </div>
        ))}
      </Card>

      <Card title="Sync Rule — Rule 19 (Mandatory End of Every Session)" accent={C.warning}>
        <div style={{ color: C.muted, fontSize: 12, lineHeight: 1.8 }}>
          Update ALL 12 managed files at end of every session — in this order:
        </div>
        {[
          { n: '1. HEAD.md',                       d: 'First — single state source. Update commit, smoke count, session name.', c: C.green },
          { n: '2. sessions-active.js',             d: 'Set status done, add date+commit, set next session to next, add SCENARIO_MATRIX entries', c: C.green },
          { n: '3. HANDOVER.md',                    d: 'Update header line, session row, next session pointer', c: C.green },
          { n: '4. CLAUDE.md',                      d: 'Update current session + last commit hash + session history table', c: C.info },
          { n: '5. CHANGELOG.md',                   d: 'Add session row: name, date, commit, what was built', c: C.info },
          { n: '6. RoadmapPage.jsx',                d: 'Update session badge + smoke badge in header', c: C.warning },
          { n: '7. SESSION_LOCK.md',                d: 'Set AVAILABLE at session end (automated by apply script)', c: C.muted },
          { n: '8. CLAUDE_SESSION_PROTOCOL.md',     d: 'Paste into Claude.ai Project Instructions if updated', c: C.purple },
          { n: '9. SESSION.md',                     d: 'Update to point at NEXT session scope, D3 signal, key files', c: C.info },
          { n: '10. smoke-test.sh',                 d: 'Add new checks, update total count. Currently 418/418.', c: C.warning },
          { n: '11. TESTING.md',                    d: 'Add new test scenarios for each session feature', c: C.green },
          { n: '12. ARCHITECTURE.md',               d: 'Update if OCI/D3/infra session — unchanged for feature sessions', c: C.muted },
        ].map(item => (
          <div key={item.n} style={{ display: 'grid', gridTemplateColumns: '240px 1fr',
            gap: 12, padding: '8px 0', borderBottom: '1px solid ' + C.border + '22' }}>
            <span style={{ color: item.c, fontWeight: 700, fontSize: 12 }}>{item.n}</span>
            <span style={{ color: C.muted, fontSize: 11 }}>{item.d}</span>
          </div>
        ))}
        <div style={{ marginTop: 12, padding: '8px 12px', background: C.warning + '11',
          borderRadius: 8, border: '1px solid ' + C.warning + '33' }}>
          <span style={{ color: C.warning, fontWeight: 700, fontSize: 11 }}>Rule: </span>
          <span style={{ color: C.muted, fontSize: 11 }}>
            Every new feature must add corresponding SCENARIO_MATRIX entries.
            Smoke test count must go UP each session, never down. Currently 418/418.
            Never close a session without all 12 managed files updated.
          </span>
        </div>
      </Card>
    </div>
  );
}
